import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { tonePillClass } from "@/lib/tone";

import { getWaves, sendWave, WAVES_QUERY_KEY, type LaunchMode, type Wave } from "./api";
import {
  confirmMatches,
  executionLine,
  fmtCount,
  heldLine,
  languageLine,
  loadErrorMessage,
  mayHaveStarted,
  modeLine,
  pollInterval,
  PRESS_POLL_WINDOW_MS,
  seedBlockReason,
  sendBlockReason,
  sendErrorMessage,
  STATE_LABEL,
  stateTone,
  testLine,
} from "./format";

// Written by Claude: Launch sends (KET-8662, GATE-684 PS-1b B). Damien presses
// Send himself for each launch wave. One card per wave; Send opens a dialog that
// fires only when the wave key is typed exactly. web-backend holds every rule
// (send-once, wave order, approver allowlist); these controls only hide a press
// the server would refuse. Write buttons carry no `data-nonmutating`, so a
// viewer's PermissionGate makes them dead (AGENTS.md boundary 11), and the
// dialog mounts into an anchor INSIDE the gate, never at document.body
// (tests/dialog-portal.test.ts).

/**
 * The waves read. It polls while any press is running, and for a short window
 * after a 202 (`pollUntil`), so a re-read that still says `ready` before the
 * execution registers cannot leave Send looking pressable with nothing polling.
 */
export function useWaves(enabled = true, pollUntil = 0) {
  return useQuery({
    queryKey: WAVES_QUERY_KEY,
    queryFn: getWaves,
    enabled,
    refetchInterval: (query) => pollInterval(query.state.data?.data.waves, pollUntil, Date.now()),
  });
}

function useSend(onSent: () => void, onMaybeStarted: () => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { wave: string; seed: boolean }) =>
      sendWave(input.wave, { confirm: input.wave, seed: input.seed }),
    onSuccess: onSent,
    // No answer, a gateway timeout or start_unconfirmed may still have started a run: poll as after a 202.
    onError: (error) => {
      if (mayHaveStarted(error)) onMaybeStarted();
    },
    // Re-read on a refusal too: a 409 means another press changed the wave, and the card must show it.
    onSettled: () => client.invalidateQueries({ queryKey: WAVES_QUERY_KEY }),
  });
}

/**
 * Mounted only while open, so the typed key starts empty every time. It cannot
 * be closed while the POST is in flight: the request has already gone, so a
 * "Cancel" would cancel nothing and throw the outcome away.
 */
export function SendDialog({
  wave,
  seed,
  live,
  container,
  onClose,
  onSent,
}: {
  wave: Wave;
  seed: boolean;
  /** False in dry mode: the press is a preview that emails nobody. */
  live: boolean;
  /** The in-gate anchor the portal mounts into. */
  container: HTMLElement;
  onClose: () => void;
  onSent: () => void;
}) {
  const [typed, setTyped] = useState("");
  const send = useSend(() => {
    onSent();
    onClose();
  }, onSent);
  const matches = confirmMatches(typed, wave);
  // After an unconfirmed start the run may exist: the dialog never offers a second press.
  const unconfirmed = send.isError && mayHaveStarted(send.error);
  const inputId = `confirm-${wave.wave}-${seed ? "seed" : "live"}`;
  const close = () => {
    if (!send.isPending) onClose();
  };

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent container={container} showCloseButton={!send.isPending}>
        <DialogHeader>
          <DialogTitle>{seed ? `Send seed test of ${wave.label}` : `Send ${wave.label}`}</DialogTitle>
          <DialogDescription>
            {seed
              ? "Sends this wave's email to its damien+ seed address only. No customer receives it."
              : live
                ? `Sends to ${fmtCount(wave.audience)} customers (${languageLine(wave.by_language)}). This cannot be undone and each wave sends once.`
                : `Dry run for ${fmtCount(wave.audience)} customers (${languageLine(wave.by_language)}). Nobody is emailed and it does not count as the wave's send.`}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (matches && !send.isPending && !unconfirmed) send.mutate({ wave: wave.wave, seed });
          }}
        >
          <Label htmlFor={inputId}>
            Type <span className="font-mono font-bold">{wave.wave}</span> to confirm
          </Label>
          <Input
            id={inputId}
            value={typed}
            autoComplete="off"
            spellCheck={false}
            disabled={send.isPending}
            onChange={(event) => setTyped(event.target.value)}
          />
          {send.isError ? (
            <Alert>
              <AlertTitle>{unconfirmed ? "Not confirmed" : "Not sent"}</AlertTitle>
              <AlertDescription>{sendErrorMessage(send.error)}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" data-nonmutating disabled={send.isPending} onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={!matches || send.isPending || unconfirmed}>
              {send.isPending ? "Starting…" : seed ? "Send seed test" : `Send ${wave.label}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-ks-grey-1">{label}</span>
      <span className="text-lg font-bold text-ks-ink tabular-nums">{value}</span>
    </div>
  );
}

export function WaveCard({
  wave,
  waves,
  mode,
  stale,
  pressedUntil,
  dialogAnchor,
  onSent,
}: {
  wave: Wave;
  waves: Wave[];
  mode: LaunchMode;
  /** True while the stale-data alert shows: Send stays shut until a fresh read. */
  stale: boolean;
  /** Until when this wave's last press holds Send shut (tab-level, outlives the dialog). */
  pressedUntil: number;
  dialogAnchor: HTMLElement | null;
  onSent: () => void;
}) {
  const [dialog, setDialog] = useState<"live" | "seed" | null>(null);
  const blocked = sendBlockReason(wave, waves, mode, stale, pressedUntil);
  const seedBlocked = seedBlockReason(wave, stale);
  const live = modeLine(mode).live;
  const held = heldLine(wave);
  const test = testLine(wave.last_test);

  return (
    <Card data-wave={wave.wave}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span className="text-ks-ink">{wave.label}</span>
          <Badge variant="outline" className={tonePillClass(stateTone(wave.state))} data-state={wave.state}>
            {STATE_LABEL[wave.state]}
          </Badge>
        </CardTitle>
        <CardDescription className="font-mono text-xs">{wave.wave}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid grid-cols-3 gap-4">
          <Figure label="Audience" value={fmtCount(wave.audience)} />
          <Figure label="Sent" value={fmtCount(wave.sent)} />
          <Figure label="Failed" value={fmtCount(wave.failed)} />
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <span className="text-ks-grey-1">
            Languages: <span className="text-ks-ink">{languageLine(wave.by_language)}</span>
          </span>
          {held ? <span className="text-ks-grey-1">{held}</span> : null}
          <span className="text-ks-grey-1">
            Last send: <span className="break-all text-ks-ink">{executionLine(wave)}</span>
          </span>
          {test ? (
            <span className="text-ks-grey-1">
              Last test: <span className="break-all text-ks-ink">{test}</span>
            </span>
          ) : null}
        </div>
        {wave.state === "failed" ? (
          <Alert>
            <AlertTitle>The last send failed</AlertTitle>
            <AlertDescription>
              {fmtCount(wave.failed)} failed, {fmtCount(wave.sent)} sent. Check the execution before pressing again.
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
      <CardFooter className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
        <Button disabled={blocked !== null} onClick={() => setDialog("live")}>
          Send {wave.label}
        </Button>
        <Button variant="outline" disabled={seedBlocked !== null} onClick={() => setDialog("seed")}>
          Send seed test
        </Button>
        {blocked ? <span className="text-sm text-ks-grey-1">{blocked}</span> : null}
      </CardFooter>
      {dialog !== null && dialogAnchor !== null && (
        <SendDialog
          wave={wave}
          seed={dialog === "seed"}
          live={live}
          container={dialogAnchor}
          onClose={() => setDialog(null)}
          onSent={onSent}
        />
      )}
    </Card>
  );
}

export function LaunchSendsTab() {
  const [pollUntil, setPollUntil] = useState(0);
  // Per wave: until when its last press holds Send shut. Lives here so Cancel + reopen cannot clear it.
  const [pressedUntil, setPressedUntil] = useState<Record<string, number>>({});
  const [, setTick] = useState(0);
  // Re-render once the window closes, so Send re-opens even if the last poll landed just before it.
  useEffect(() => {
    if (pollUntil <= Date.now()) return;
    const timer = setTimeout(() => setTick((t) => t + 1), pollUntil - Date.now() + 50);
    return () => clearTimeout(timer);
  }, [pollUntil]);
  const pressed = (key: string) => {
    const until = Date.now() + PRESS_POLL_WINDOW_MS;
    setPollUntil(until);
    setPressedUntil((prev) => ({ ...prev, [key]: until }));
  };
  // The anchor lives inside the shell's PermissionGate, so the dialog's portal does too.
  const [dialogAnchor, setDialogAnchor] = useState<HTMLDivElement | null>(null);
  const query = useWaves(true, pollUntil);

  if (!query.data) {
    if (query.isError) {
      return (
        <Alert>
          <AlertTitle>Launch sends unavailable</AlertTitle>
          <AlertDescription>{loadErrorMessage(query.error)}</AlertDescription>
        </Alert>
      );
    }
    return (
      <div className="grid gap-4 md:grid-cols-2" aria-busy="true">
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const { waves, mode } = query.data.data;
  const line = modeLine(mode);

  return (
    <div className="flex flex-col gap-4">
      <div ref={setDialogAnchor} className="contents" />
      {query.isError ? (
        // A failed re-read keeps the last cards on screen: a blip mid-send must not hide the live counts.
        <Alert>
          <AlertTitle>Showing the last figures read</AlertTitle>
          <AlertDescription>{loadErrorMessage(query.error)} These cards may be out of date.</AlertDescription>
        </Alert>
      ) : null}
      {line.live ? (
        <p className="m-0 text-sm text-ks-ink">{line.text}</p>
      ) : (
        <Alert>
          <AlertTitle>Test mode</AlertTitle>
          <AlertDescription>{line.text}</AlertDescription>
        </Alert>
      )}
      {waves.length === 0 ? (
        <p className="m-0 text-sm text-ks-grey-1">No launch waves are set up.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {waves.map((wave) => (
            <WaveCard
              key={wave.wave}
              wave={wave}
              waves={waves}
              mode={mode}
              stale={query.isError}
              pressedUntil={pressedUntil[wave.wave] ?? 0}
              dialogAnchor={dialogAnchor}
              onSent={() => pressed(wave.wave)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
